import { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Container, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import TotalCustomers from 'src/components/dashboard/TotalCustomers';
import { getRecords } from 'src/api';
import { CONSTANTS, STRINGS } from 'src/commons';
import { useState } from 'react';
import { loaderReducerAction } from 'src/redux/reducers/loader';
import { commonReducerAction } from 'src/redux/reducers/common';
import { getMembershipPlan } from 'src/api/membership';
import { getTrainer } from 'src/api/trainer';
import { getBatch } from 'src/api/batches';
import { trainersReducerAction } from 'src/redux/reducers/trainer';
import { batchesReducerAction } from 'src/redux/reducers/batches';
import { membershipPlanReducerAction } from 'src/redux/reducers/membershipPlan';
import { getMembers } from 'src/api/member';
import { membersReducerAction } from 'src/redux/reducers/members';
import { dietPlanReducerAction } from 'src/redux/reducers/dietPlan'
import { getDietPlans } from 'src/api/dietPlan'

const Records = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { startLoading, stopLoading } = loaderReducerAction;
  const { logoutUser } = commonReducerAction;
  const { updateMembershipPlans } = membershipPlanReducerAction;
  const { updateMembers } = membersReducerAction;
  const { updateBatches } = batchesReducerAction;
  const { updateTrainers } = trainersReducerAction;
  const { updateDietPlans } = dietPlanReducerAction;
  const { user, gymId } = useSelector((state) => state.common);
  const { membersList } = useSelector((state) => state.members);
  const { trainerList } = useSelector((state) => state.trainer);
  dayjs.extend(customParseFormat);

  const [records, setRecords] = useState({
    totalMembers: 0,
    thisMonthMembers: 0,
    membershipsExpiringThisMonth: 0,
    membershipsExpired: 0,
    totalQueries: 0,
    todayFollowupQueries: 0,
    pendingPaymentMembers: 0
  });

  useEffect(() => {
    if (gymId && user) {
      dispatch(startLoading());
      getMembers(user, gymId)
        .then((res) => {
          if (res.status === CONSTANTS.STATUS_CODES.FORBIDDEN)
            dispatch(logoutUser());
          if (res.status === CONSTANTS.STATUS_CODES.SUCCESS) {
            dispatch(updateMembers(res?.data?.allMembers));
          }
          dispatch(stopLoading());
        })
        .catch((err) => {
          dispatch(stopLoading());
          console.log(err);
        });
      getMembershipPlan(user, gymId)
        .then((res) => {
          if (res.status === CONSTANTS.STATUS_CODES.FORBIDDEN)
            dispatch(logoutUser());
          if (res.status === CONSTANTS.STATUS_CODES.SUCCESS) {
            dispatch(updateMembershipPlans(res?.data?.data));
          }
          dispatch(stopLoading());
        })
        .catch((err) => {
          dispatch(stopLoading());
          console.log(err);
        });
      dispatch(startLoading());
      getBatch(user, gymId)
        .then((res) => {
          if (res.status === CONSTANTS.STATUS_CODES.FORBIDDEN)
            dispatch(logoutUser());
          if (res.status === CONSTANTS.STATUS_CODES.SUCCESS) {
            dispatch(updateBatches(res.data));
          }
          dispatch(stopLoading());
        })
        .catch((err) => {
          dispatch(stopLoading());
          console.log(err);
        });
      dispatch(startLoading());
      getTrainer(user, gymId)
        .then((res) => {
          if (res.status === CONSTANTS.STATUS_CODES.FORBIDDEN)
            dispatch(logoutUser());
          if (res.status === CONSTANTS.STATUS_CODES.SUCCESS) {
            dispatch(updateTrainers(res.data));
          }
          dispatch(stopLoading());
        })
        .catch((err) => {
          dispatch(stopLoading());
          console.log(err);
        });
        dispatch(startLoading());
        getDietPlans(user, gymId)
          .then((res) => {
            if (res.status === CONSTANTS.STATUS_CODES.FORBIDDEN) logoutUser();
            if (res.status === CONSTANTS.STATUS_CODES.SUCCESS) {
              dispatch(updateDietPlans(res.data?.dietPlans));
            }
            dispatch(stopLoading());
          })
          .catch((err) => {
            dispatch(stopLoading());
            console.log(err);
          });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, gymId]);

  useEffect(() => {
    if (user && gymId) {
      dispatch(startLoading());
      getRecords(user, gymId)
        .then((res) => {
          if (res.status === CONSTANTS.STATUS_CODES.FORBIDDEN)
            dispatch(logoutUser());
          if (res.status === CONSTANTS.STATUS_CODES.SUCCESS)
            setRecords(res.data);
        })
        .catch((err) => console.log(err, 'ERR'))
        .finally(() => dispatch(stopLoading()));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gymId, user]);
  const expiringMemberships = membersList
    ? membersList.filter(
        (member) =>
          dayjs(new Date()) <=
            dayjs(member.membership_end_date, 'DD-MM-YYYY') &&
          dayjs(member.membership_end_date, 'DD-MM-YYYY') <=
            dayjs().endOf('month')
      ).length
    : 0;

  const membershipThisMonth = membersList
    ? membersList.filter(
        (member) =>
          dayjs(member.membership_start_date, 'DD-MM-YYYY') <
          dayjs().endOf('month')
      ).length
    : 0;

  const expiredMemberships = membersList
    ? membersList.filter(
        (member) =>
          dayjs(member.membership_end_date, 'DD-MM-YYYY') < dayjs(new Date())
      ).length
    : 0;

  return (
    <>
      <Helmet>
        <title>Records | GMS</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 3
        }}
      >
        <Container maxWidth={false}>
          <Grid container spacing={2}>
            <Grid item lg={5} sm={6} xl={4} xs={12}>
              <TotalCustomers
                handleClick={() =>
                  navigate(CONSTANTS.ROUTES.PRIVATE_ROUTES.MEMBERS)
                }
                title={STRINGS.TOTAL_MEMBERS}
                quantity={membersList.length ? membersList.length : 0}
              />
            </Grid>
            <Grid item lg={5} sm={6} xl={4} xs={12}>
              <TotalCustomers
                handleClick={() =>
                  navigate(CONSTANTS.ROUTES.PRIVATE_ROUTES.QUERIES)
                }
                title={STRINGS.ALL_QUERIES}
                quantity={records?.totalQueries}
              />
            </Grid>
            <Grid item lg={5} sm={6} xl={4} xs={12}>
              <TotalCustomers
                handleClick={() =>
                  navigate(CONSTANTS.ROUTES.PRIVATE_ROUTES.PENDING_PAYMENT)
                }
                title={STRINGS.PENDING_PAYMENT}
                quantity={records?.pendingPaymentMembers}
              />
            </Grid>
            <Grid item lg={5} sm={6} xl={4} xs={12}>
              <TotalCustomers
                handleClick={() =>
                  navigate(
                    CONSTANTS.ROUTES.PRIVATE_ROUTES.THIS_MONTH_MEMBERSHIP
                  )
                }
                title={STRINGS.THIS_MONTH_MEMBERSHIP}
                quantity={membershipThisMonth}
              />
            </Grid>
            <Grid item lg={5} sm={6} xl={4} xs={12}>
              <TotalCustomers
                handleClick={() =>
                  navigate(
                    CONSTANTS.ROUTES.PRIVATE_ROUTES
                      .MEMBERSHIP_EXPIRED_THIS_MONTH
                  )
                }
                title={STRINGS.MEMBERSHIPS_EXPIRING}
                quantity={expiringMemberships}
              />
            </Grid>
            <Grid item lg={5} sm={6} xl={4} xs={12}>
              <TotalCustomers
                handleClick={() =>
                  navigate(CONSTANTS.ROUTES.PRIVATE_ROUTES.EXPIRED_MEMBERSHIP)
                }
                title={STRINGS.EXPIRED_MEMBERSHIPS}
                quantity={expiredMemberships}
              />
            </Grid>
            <Grid item lg={5} sm={6} xl={4} xs={12}>
              <TotalCustomers
                handleClick={() =>
                  navigate(CONSTANTS.ROUTES.PRIVATE_ROUTES.QUERIES_FOLLOWUPS)
                }
                title={STRINGS.TOTAL_FOLLOW_UPS}
                quantity={records?.todayFollowupQueries}
              />
            </Grid>
            <Grid item lg={5} sm={6} xl={4} xs={12}>
              <TotalCustomers
                handleClick={() =>
                  navigate(CONSTANTS.ROUTES.PRIVATE_ROUTES.TRAINER)
                }
                title={STRINGS.TOTAL_TRAINER}
                quantity={trainerList.length}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default Records;
